import type { Icon } from "@phosphor-icons/react";
import { InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { Button } from "design-system/button";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type WarningBannerAction = {
  label: string;
  onClick: () => void;
};

type WarningBannerVariant = "info" | "warning";

const VARIANTS = {
  info: {
    container: "border-blue-200 bg-blue-50 text-blue-900",
    defaultIcon: InfoIcon,
    description: "",
    icon: "text-blue-600",
    role: "status",
    weight: "regular",
  },
  warning: {
    container: "border-amber-500/50 bg-amber-50 text-amber-900",
    defaultIcon: WarningIcon,
    description: "text-amber-900/90",
    icon: "text-amber-600",
    role: "alert",
    weight: "fill",
  },
} as const;

type WarningBannerProps = {
  action?: WarningBannerAction;
  className?: string;
  "data-testid"?: string;
  description: ReactNode;
  icon?: Icon;
  // Present only for banners the user may put away; the caller decides for how long.
  onDismiss?: () => void;
  title?: ReactNode;
  variant?: WarningBannerVariant;
};

// Reusable banner: icon + optional title/description on the left, an optional CTA (and optional dismiss) on the
// right. The "warning" (amber, assertive) and "info" (blue, polite) variants share one layout and differ only in
// color, default icon, and ARIA role; the palettes match the design-system Alert variants so they read as the same
// system. Hand-rolled like NetworkUpdateBanner because the Alert grid carries no right-aligned action row.
export function WarningBanner({
  action,
  className,
  "data-testid": dataTestId,
  description,
  icon,
  onDismiss,
  title,
  variant = "warning",
}: WarningBannerProps) {
  const styles = VARIANTS[variant];
  const Icon = icon ?? styles.defaultIcon;
  return (
    <div
      className={cn("flex items-center justify-between gap-3 rounded-lg border px-4 py-3", styles.container, className)}
      data-testid={dataTestId}
      role={styles.role}
    >
      <div className="flex min-w-0 items-start gap-3">
        <Icon aria-hidden className={cn("mt-0.5 size-4 shrink-0", styles.icon)} weight={styles.weight} />
        <div className="min-w-0">
          {title && <div className="text-sm font-medium">{title}</div>}
          <div className={cn("text-sm", styles.description)}>{description}</div>
        </div>
      </div>
      {(action || onDismiss) && (
        <div className="flex shrink-0 items-center gap-2">
          {action && (
            <Button onClick={action.onClick} size="sm" type="button">
              {action.label}
            </Button>
          )}
          {onDismiss && (
            <Button aria-label="Dismiss" onClick={onDismiss} size="icon" type="button" variant="ghost">
              <XIcon className="size-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
